import { H1, H2 } from '@berlitz/globals'
import Modal from '@berlitzplatforms/micro.ui.modal'
import React, { useState } from 'react'
import { AboutMe, Actions, AvatarWrapper, Fave, Header, Info } from './style'
import PortalInstructorProfile from '@classes/InstructorProfile'
import LoveIt from '@berlitz/streamline-icons/lib/bold/22-Social-Medias-Rewards-Rating/05-Love-it/LoveIt'
import LoveItAlt from '@berlitz/streamline-icons/lib/bold/22-Social-Medias-Rewards-Rating/05-Love-it/LoveItAlternate'
import MicrophoneSingMan from '@berlitz/streamline-icons/lib/regular/14-Music-Audio/12-Microphone/MicrophoneSingMan'
import MessagesBubbleSquareText from '@berlitz/streamline-icons/lib/regular/21-Messages-Chat-Smileys/02-Messages-Speech-Bubbles/MessagesBubbleSquareText'
import SchoolTeacher from '@berlitz/streamline-icons/lib/regular/36-School-Learning/01-School/SchoolTeacher'
import Disable from '@berlitz/streamline-icons/lib/bold/01-Interface-Essential/23-Delete/Disable'
import { useBlockedInstructors, useFave } from '@layouts/Scheduling/Instructors/context'
import { sortBy } from 'lodash'
import Spacer from '@berlitz/spacer'
import Avatar from '@berlitzplatforms/micro.ui.avatar'
import {
  ProfileInfoAttr,
  ProfileInfoAttrItem,
  ProfileInfoAttrItemIcon,
  ProfileInfoAttrItemInfo,
} from '@layouts/Scheduling/Instructors/Profile/style'
import { useIntl } from 'react-intl'
import { getMessages } from '../../constants'
import HR from '@components/HR/HR'
import IconWrapper from '@components/IconWrapper'
import BlockModal from '@layouts/Scheduling/Instructors/BlockModal'

interface IProfileModal {
  open: boolean
  onClose: () => void
  Profile: PortalInstructorProfile | null
  onCompleted?: (id: string) => void
}

const ProfileModal: React.FC<IProfileModal> = ({
  open = false,
  onClose = () => {},
  onCompleted = () => {},
  Profile,
}) => {
  const intl = useIntl()
  const fave = useFave()
  const blocked = useBlockedInstructors()
  const messages = getMessages()
  const isFaved = fave.isFaved(Profile?.InstructorId)
  const [openBlockModal, setOpenBlockModal] = useState(false)

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Header>
          <H1 disableMargin>{Profile?.Name}</H1>

          <Actions>
            <Fave
              isFaved={isFaved}
              isDisabled={!fave.enableActions}
              onClick={() => {
                if (fave.enableActions) {
                  const newFaves = fave.faveInstructors.filter(({ InstructorId: Id }) => Id !== Profile?.InstructorId)

                  if (isFaved) {
                    fave.setFaveInstructors(newFaves)
                    fave.deleteRelatedInstructors({
                      variables: fave.getPayload('delete', [Profile?.profile!]),
                    })
                  } else {
                    fave.setFaveInstructors(sortBy([...newFaves, Profile?.profile!], ['Name']))
                    fave.putRelatedInstructors({
                      variables: fave.getPayload('put', [Profile?.profile!]),
                    })
                  }
                }
              }}
            >
              {isFaved ? <LoveIt /> : <LoveItAlt />}
            </Fave>

            {blocked.allowed ? (
              <div
                className={`block-icon ${blocked.isBlocked(Profile?.InstructorId) ? 'blocked' : ''} ${blocked.enableActions ? '' : 'blocked-disabled'}`}
                onClick={() => {
                  if (blocked.enableActions) {
                    if (blocked.isBlocked(Profile?.profile?.InstructorId) && Profile?.profile) {
                      blocked.unblockInstructors([Profile?.profile])
                    } else {
                      blocked.setSelectedInstructor(Profile?.profile!)
                      setOpenBlockModal(true)
                    }
                  }
                }}
              >
                <Disable />
              </div>
            ) : null}
          </Actions>
        </Header>

        <Spacer size="lg" />

        <Info>
          <IconWrapper>
            <AvatarWrapper>
              <Avatar type="profile" size="90px" img={Profile?.Avatar} />
            </AvatarWrapper>
          </IconWrapper>

          <ProfileInfoAttr style={{ marginBottom: 0, alignSelf: 'center' }}>
            <ProfileInfoAttrItem>
              <ProfileInfoAttrItemIcon>
                <MicrophoneSingMan />
              </ProfileInfoAttrItemIcon>
              <ProfileInfoAttrItemInfo>
                {intl.formatMessage(messages.speaks)} <strong>{Profile?.PrimaryLanguage}</strong>
              </ProfileInfoAttrItemInfo>
            </ProfileInfoAttrItem>

            {Profile?.OtherLanguages && (
              <ProfileInfoAttrItem>
                <ProfileInfoAttrItemIcon>
                  <MessagesBubbleSquareText />
                </ProfileInfoAttrItemIcon>
                <ProfileInfoAttrItemInfo>
                  {intl.formatMessage(messages.alsoSpeaks)} <strong>{Profile?.OtherLanguages}</strong>
                </ProfileInfoAttrItemInfo>
              </ProfileInfoAttrItem>
            )}

            {Profile?.TeachingSince && (
              <ProfileInfoAttrItem>
                <ProfileInfoAttrItemIcon>
                  <SchoolTeacher />
                </ProfileInfoAttrItemIcon>
                <ProfileInfoAttrItemInfo>
                  {intl.formatMessage(messages.teachingSince)} <strong>{Profile?.TeachingSince}</strong>
                </ProfileInfoAttrItemInfo>
              </ProfileInfoAttrItem>
            )}
          </ProfileInfoAttr>
        </Info>

        <Spacer size="md" />
        <HR />

        <H2>{intl.formatMessage(messages.aboutMe)}</H2>
        <AboutMe>{Profile?.Description}</AboutMe>

        {blocked.isBlocked(Profile?.InstructorId) ? (
          <>
            {Profile?.profile?.BlockedReason ? (
              <>
                <Spacer size="lg" />
                <H2>
                  {intl.formatMessage({
                    id: 'Reason for block',
                    defaultMessage: 'Reason for block',
                  })}
                </H2>
                <AboutMe>{Profile?.profile?.BlockedReason}</AboutMe>
              </>
            ) : null}

            {Profile?.profile?.SchedulingLevel ? (
              <>
                <Spacer size="lg" />
                <H2>
                  {intl.formatMessage({
                    id: 'Blocked for:',
                    defaultMessage: 'Blocked for:',
                  })}
                </H2>
                <AboutMe>{blocked.getSchedulingLevelLabel(Profile?.profile?.SchedulingLevel)}</AboutMe>
              </>
            ) : null}
          </>
        ) : null}
      </Modal>

      <BlockModal
        open={openBlockModal && !!blocked.selectedInstructor}
        onClose={() => {
          blocked.setSelectedInstructor(undefined)
          setOpenBlockModal(false)
        }}
        onCompleted={() => onCompleted(Profile?.InstructorId!)}
      />
    </>
  )
}

export default ProfileModal
